import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardActions from '@mui/material/CardActions';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { css } from '@emotion/react';

import type { RepresentativeTypeId, UserRoleId } from '@e-socna/data-layer';

import { Avatar } from '../Avatar';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { ACTOR_CARD_SIZE, ACTOR_CARD_SX } from './constants/card.constant';
import { actorableRuleDescriptionsOf } from './utils/actorable-rule-descriptions.util';
import type { ActorsCmp_ActorableRules } from './fragments/actorable-rules.fragment';

interface Props {
  readonly onHideCard: () => void;
  readonly rules: ActorsCmp_ActorableRules;
  readonly isInternal: boolean;
}

const cardTitle = 'Règles de gestion';
const closeLabel = 'Fermer';

export const RulesCard = ({
  isInternal,
  onHideCard: handleHideCard,
  rules,
}: Readonly<Props>) => {
  const { representativeTypesIdMap, userRolesCodeMap, userRolesIdMap } =
    useReferencesContext();
  const descriptions = actorableRuleDescriptionsOf(
    rules,
    isInternal ? 'internal' : 'external',
    {
      baseRoleLabel: userRolesCodeMap.Base.label,
      roleLabelOf: (roleId) =>
        userRolesIdMap[roleId as UserRoleId]?.label ?? 'Inconnu',
      representativeTypeLabelOf: (typeId) =>
        representativeTypesIdMap[typeId as RepresentativeTypeId]?.label ??
        'Inconnu',
    },
  );

  return (
    <Grid size={ACTOR_CARD_SIZE} sx={ACTOR_CARD_SX}>
      <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          action={
            <Tooltip title={closeLabel}>
              <IconButton aria-label={closeLabel} onClick={handleHideCard}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          }
          avatar={<Avatar icon={<LightbulbIcon />} label={cardTitle} />}
          css={css`
            .MuiCardHeader-action {
              display: contents;
            }
          `}
        />
        <CardContent
          sx={{
            flexGrow: 1,
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? theme.palette.grey[800]
                : theme.palette.grey[200],
          }}
        >
          <Typography color="text.secondary" gutterBottom variant="overline">
            {cardTitle}
          </Typography>
          {descriptions.map((description) => (
            <Typography
              color="text.secondary"
              key={description}
              variant="body2"
            >
              - {description}
            </Typography>
          ))}
          {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
          {!descriptions.length && (
            <Typography color="text.secondary" variant="body2">
              Aucune règle particulière pour ces acteurs
            </Typography>
          )}
        </CardContent>
        <CardActions>
          <Button onClick={handleHideCard} size="medium" variant="outlined">
            J&apos;ai compris
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );
};
